import React from 'react';
import { Box, Typography, Tooltip, Theme } from '@mui/material';

export type StatusType = 'success' | 'warning' | 'error' | 'info' | 'neutral';

interface StatusIndicatorProps {
    status: StatusType;
    label?: string;
    tooltip?: string;
    size?: number;
    labelSize?: 'small' | 'medium' | 'large';
}

const getStatusColor = (status: StatusType, theme: Theme) => {
    switch (status) {
        case 'success':
            return theme.palette.success.main;
        case 'warning':
            return theme.palette.warning.main;
        case 'error':
            return theme.palette.error.main;
        case 'info':
            return theme.palette.primary.main;
        case 'neutral':
        default:
            return theme.palette.grey[500];
    }
};

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
    status,
    label,
    tooltip,
    size = 10,
    labelSize = 'medium',
}) => {
    const indicator = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Status dot */}
            <Box
                sx={(theme) => ({
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(status, theme),
                    boxShadow: `0 0 ${size / 2}px ${getStatusColor(status, theme)}`,
                    transition: 'all 0.3s ease',
                })}
            />
            
            {/* Optional label */}
            {label && (
                <Typography 
                    variant={labelSize === 'small' ? 'caption' : labelSize === 'large' ? 'body1' : 'body2'} 
                    sx={{ fontWeight: 500 }}
                >
                    {label}
                </Typography>
            )}
        </Box>
    );

    if (tooltip) {
        return (
            <Tooltip title={tooltip} arrow>
                {indicator}
            </Tooltip>
        );
    }

    return indicator;
};

export default StatusIndicator; 